<script>
import useTools from "@/use/Tools.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import axios from "axios";


export default {
    setup() {
        const {mxRules} = useValidation();
        const {$route} = useTools();
        const {dialog, loading} = useSharedData();
        const {getBack, translateMonth, priceFormat} = useSharedMethod();

        const months = ref([]);

        function findMonthsByWorker(workerId) {
            loading.value = true;
            axios.get(`/outputState/workers/months/${workerId}`).then(response => {
                months.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        const monthDialog = ref(false);
        const ats = ref({
            version: "FR",
            months: [],
            restartWorkingOption: 'RESTART',
            lastWorkingDay: null,
            restartWorkingDay: null,
            notRestartWorkingDay: null,
            sickHoliday: 'LESS_THAN_6_MONTHS',
            sickHolidayDays: null,
            sickHolidayHours: null,
            sickHolidayFrom: null,
            sickHolidayTo: null,
            printDate: null,
            madeIn: null,
            signatoryStatus: null,
            dailyHourlyVolume: null,
        });

        function isSelected(month) {
            return ats.value.months.some(obj => obj.id === month.id);
        }

        function removeMonth(month) {
            ats.value.months = ats.value.months.filter(obj => obj.id !== month.id);
        }

        function addMonth(month) {
            ats.value.months.push(month);
            ats.value.months.sort((a, b) => a.annexMonth - b.annexMonth);
        }

        function select(month) {
            if (isSelected(month)) {
                removeMonth(month);
            } else {
                addMonth(month);
            }
        }

        function exportAts() {
            loading.value = true;
            axios.post(`/outputState/workers/exportAts/${$route.params.id}`, ats.value, {
                responseType: "blob",
            }).then((response) => {
                const url = URL.createObjectURL(response.data);
                window.open(url);
            }).finally(() => {
                loading.value = false;
            });

        }

        onMounted(() => findMonthsByWorker($route.params.id));

        return {
            mxRules,
            dialog,
            loading,
            months,
            monthDialog,
            ats,
            getBack,
            translateMonth,
            priceFormat,
            isSelected,
            select,
            removeMonth,
            exportAts
        }
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="900"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('worker.ats.ats_print_work_salary')"/>
                <v-card-text class="ml-n5 mr-n5">
                    <v-form ref="form" lazy-validation>
                        <v-container fluid>
                            <v-row dense>
                                <v-radio-group row v-model="ats.version">
                                    <v-radio value="FR" :label="$t('common.french')"></v-radio>
                                    <v-radio value="AR" :label="$t('common.arab')"></v-radio>
                                </v-radio-group>
                            </v-row>
                            <w-fieldset
                                :title="$t('worker.ats.pay_print')"
                                show-add-action
                                @add="monthDialog = true"
                            >
                                <v-simple-table dense fixed-header height="200">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left"> {{ $t('common.month') }}</th>
                                            <th class="text-left"> {{ $t('worker.ats.days_number') }}</th>
                                            <th class="text-left"> {{ $t('worker.ats.absence_reason') }}</th>
                                            <th class="text-left"> {{ $t('worker.ats.costisable') }}</th>
                                            <th class="text-left"> {{ $t('worker.ats.security_social') }}</th>
                                            <th class="text-left"></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="item in ats.months"
                                            :key="item.name"
                                        >
                                            <td class="text-left">{{ translateMonth(item.annexMonth, true) }}</td>
                                            <td class="text-left">
                                                <v-edit-dialog
                                                    :return-value.sync="item.daysNumber"
                                                >
                                                    {{ item.daysNumber }}
                                                    <template v-slot:input>
                                                       <v-container fluid>
                                                           <w-text-field
                                                               v-model="item.daysNumber"
                                                               label="Modifier"
                                                           />
                                                       </v-container>
                                                    </template>
                                                </v-edit-dialog>
                                            </td>
                                            <td class="text-left"></td>
                                            <td class="text-left">{{ priceFormat(item.baseCotisable) }}</td>
                                            <td class="text-left">{{ priceFormat(item.retenueSecuriteSociale) }}</td>
                                            <td class="text-left">
                                                <v-btn icon color="red" @click="removeMonth(item)">
                                                    <v-icon>mdi-trash-can</v-icon>
                                                </v-btn>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('worker.ats.necessary_study')">
                                <v-row dense>
                                    <v-col cols="4">
                                        <w-label :label="$t('worker.ats.last_working_date')"/>
                                        <w-date-picker v-model="ats.lastWorkingDay"/>
                                    </v-col>
                                </v-row>
                                <v-row dense v-if="ats.lastWorkingDay">
                                    <v-col cols="6">

                                        <v-radio-group v-model="ats.restartWorkingOption">
                                            <v-radio class="mb-15" :value="'RESTART'">
                                                <template v-slot:label>
                                                    <strong class="teal--text">
                                                        {{ $t('worker.ats.reworking') }}
                                                    </strong>
                                                </template>
                                            </v-radio>
                                            <v-radio class="mb-n4" :value="'NOT_YET'">
                                                <template v-slot:label>
                                                    <strong class="red--text">
                                                        {{ $t('worker.ats.not_reworking') }}
                                                    </strong>
                                                </template>
                                            </v-radio>
                                        </v-radio-group>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-row dense v-if="ats.lastWorkingDay">
                                            <v-col cols="12">
                                                <w-label :label="$t('common.the')"/>
                                                <w-date-picker
                                                    v-model="ats.restartWorkingDay"
                                                    :disabled="ats.restartWorkingOption !== 'RESTART'"
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <w-label :label="$t('common.nowadays')"/>
                                                <w-date-picker
                                                    v-model="ats.notRestartWorkingDay"
                                                    :disabled="ats.restartWorkingOption !== 'NOT_YET'"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <v-row>
                                <v-col cols="12">
                                    <v-radio-group v-model="ats.sickHoliday">
                                        <v-radio :value="'LESS_THAN_6_MONTHS'">
                                            <template v-slot:label>
                                                <strong>
                                                    {{ $t('worker.ats.sick_holiday_LESS_THAN_6_MONTHS') }}
                                                </strong>
                                            </template>
                                        </v-radio>
                                        <v-radio :value="'MORE_THAN_6_MONTHS'">
                                            <template v-slot:label>
                                                <strong>
                                                    {{ $t('worker.ats.sick_holiday_MORE_THAN_6_MONTHS') }}
                                                </strong>
                                            </template>
                                        </v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                            <w-fieldset>
                                <v-container fluid>
                                    <v-row dense>
                                        <v-col cols="12">
                                            <strong> {{ $t('worker.ats.worker_for') }} </strong>
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="3">
                                            <w-label :label="$t('common.days')"/>
                                            <w-text-field
                                                v-model="ats.sickHolidayDays"
                                                type="number"
                                                :rules="[mxRules.lessThan(2)]"
                                            />
                                        </v-col>
                                        <v-col cols="3">
                                            <w-label :label="$t('common.hours')"/>
                                            <w-text-field
                                                v-model="ats.sickHolidayHours"
                                                type="number"
                                                :rules="[mxRules.lessThan(3)]"
                                            />
                                        </v-col>

                                        <v-col cols="3">
                                            <w-label :label="$t('common.from')"/>
                                            <w-date-picker v-model="ats.sickHolidayFrom"/>
                                        </v-col>
                                        <v-col cols="3">
                                            <w-label :label="$t('common.to')"/>
                                            <w-date-picker v-model="ats.sickHolidayTo"/>
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="12">
                                            <h3> {{ $t('worker.ats.depending_on_case') }} </h3>
                                        </v-col>
                                        <v-col cols="12" v-if="ats.sickHoliday === 'LESS_THAN_6_MONTHS'">
                                            <strong>
                                                {{ $t('worker.ats.sick_holiday_LESS_THAN_6_MONTHS_description_1') }}
                                                <br/><br/>
                                                {{ $t('worker.ats.sick_holiday_LESS_THAN_6_MONTHS_description_2') }}
                                            </strong>
                                        </v-col>
                                        <v-col cols="12" v-if="ats.sickHoliday === 'MORE_THAN_6_MONTHS'">
                                            <strong>
                                                {{ $t('worker.ats.sick_holiday_MORE_THAN_6_MONTHS_description') }}
                                            </strong>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('worker.ats.feet')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.print_date')"/>
                                        <w-date-picker v-model="ats.printDate"/>
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('worker.ats.daily_hourly_volume')"/>
                                        <w-text-field v-model="ats.dailyHourlyVolume"/>
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.made_in')"/>
                                        <w-text-field v-model="ats.madeIn"/>
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('worker.ats.signatory_status')"/>
                                        <w-text-field v-model="ats.signatoryStatus"/>
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-export
                        :loading="loading"
                        @press="exportAts"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="monthDialog"
            max-width="400"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('worker.ats.pay_reference')"/>
                <v-card-text>
                    <v-list dense>
                        <template v-for="(item, index) in months.slice().sort((a, b) => a.annexMonth - b.annexMonth)">
                            <v-list-item
                                :key="`m-${index}`"
                                @click="select(item)"
                            >
                                <v-list-item-title>{{ translateMonth(item.annexMonth, true) }}</v-list-item-title>
                                <v-list-item-action>
                                    <v-icon
                                        v-if="isSelected(item)"
                                        color="teal"
                                    >
                                        mdi-check-circle
                                    </v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider v-if="index < months.length - 1" :key="`d-${index}`"/>
                        </template>
                    </v-list>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="monthDialog = false"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>